import { useChartContext, yScaleFor } from '../core/chart-context';
import { stackSeries } from '../core/scales';
import type { NumericSeries } from '../core/types';

export interface BarsProps {
  series: NumericSeries[];
  /** Corner radius on the top edge */
  radius?: number;
  /** Overrides the fill per bar, e.g. threshold or per-category coloring */
  getBarColor?: (value: number, x: string | number, index: number) => string;
  /** Caps the rendered bar width in px */
  maxBarSize?: number;
  /** Stacks series on top of each other instead of grouping side by side */
  stacked?: boolean;
}

/**
 * Bar outline rounded on the top edge only — `rx` on a `<rect>` would round all four
 * corners and pill-shape short bars. Pure so it can be asserted without a DOM.
 */
export function barPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): string {
  const bottom = y + height;
  // A radius past half the width or the full height cannot be drawn as a corner.
  const r = Math.max(Math.min(radius, width / 2, height), 0);
  if (r === 0) return `M${x},${bottom} V${y} H${x + width} V${bottom} Z`;

  return (
    `M${x},${bottom} V${y + r} A${r},${r} 0 0 1 ${x + r},${y} ` +
    `H${x + width - r} A${r},${r} 0 0 1 ${x + width},${y + r} V${bottom} Z`
  );
}

/** Grouped or stacked bars with a rounded top edge, one group per x-slot. */
export const Bars = ({
  series,
  radius = 4,
  getBarColor,
  maxBarSize,
  stacked = false
}: BarsProps) => {
  const context = useChartContext();
  const { xScale, dimensions } = context;
  const domain = xScale.domain;
  const groupWidth = xScale.bandwidth;

  if (stacked) {
    const barWidth = Math.min(groupWidth, maxBarSize ?? groupWidth);
    const layers = stackSeries(series);

    return (
      <g data-slot='chart-bars'>
        {layers.map(({ series: s, bases, tops }, seriesIndex) => {
          const yScale = yScaleFor(context, s.axis);
          return domain.map((x, index) => {
            const base = bases[index];
            const top = tops[index];
            if (base === undefined || top === undefined) return null;

            const y0 = yScale(base);
            const y1 = yScale(top);
            const left = xScale.position(x) - barWidth / 2;
            // Only the outermost segment gets the rounded corner.
            const isOutermost = seriesIndex === layers.length - 1;

            return (
              <path
                key={`${seriesIndex}-${x}`}
                data-slot='chart-bar'
                d={barPath(
                  left,
                  Math.min(y0, y1),
                  Math.max(barWidth - 1, 0),
                  Math.max(Math.min(Math.abs(y0 - y1), dimensions.innerHeight), 0),
                  isOutermost ? radius : 0
                )}
                fill={getBarColor?.(top - base, x, index) ?? s.color}
              />
            );
          });
        })}
      </g>
    );
  }

  const rawWidth = groupWidth / Math.max(series.length, 1);
  const barWidth = Math.min(rawWidth, maxBarSize ?? rawWidth);
  const groupOffset = (barWidth * series.length) / 2;

  return (
    <g data-slot='chart-bars'>
      {series.map((s, seriesIndex) => {
        const yScale = yScaleFor(context, s.axis);
        const baseline = yScale(0);
        return domain.map((x, index) => {
          const value = s.values[index];
          if (value === undefined) return null;

          const y = yScale(value);
          const top = Math.min(y, baseline);
          const barHeight = Math.abs(baseline - y);
          const left = xScale.position(x) - groupOffset + seriesIndex * barWidth;

          return (
            <path
              key={`${seriesIndex}-${x}`}
              data-slot='chart-bar'
              d={barPath(
                left,
                top,
                Math.max(barWidth - 1, 0),
                Math.max(Math.min(barHeight, dimensions.innerHeight), 0),
                radius
              )}
              fill={getBarColor?.(value, x, index) ?? s.color}
            />
          );
        });
      })}
    </g>
  );
};

Bars.displayName = 'Bars';
